import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { COLORS, MOBILE } from '../../theme';
import { useContadorNotificacoes } from '../../hooks/useNotificacoes';
import { userProfilePath } from '../../constants/userRoutes';

/**
 * Navegação principal da área do cliente no celular.
 *
 * Fica na zona do polegar (rodapé) e reaproveita o espaço que o layout já
 * reservava abaixo do conteúdo. Cinco destinos fixos; Ajustes e Ajuda continuam
 * nas pílulas do topo, que também rolam.
 */
const UserBottomNav = ({ userId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { naoLidas = 0 } = useContadorNotificacoes();

  const itens = [
    { icon: DashboardRoundedIcon, label: 'Painel', path: '/user/dashboard' },
    {
      icon: ShoppingBagRoundedIcon,
      label: 'Pedidos',
      path: '/user/orders',
      tambemAtivo: ['/user/pedido/'],
    },
    { icon: PaymentsRoundedIcon, label: 'Pagamentos', path: '/user/payments' },
    {
      icon: NotificationsNoneRoundedIcon,
      label: 'Alertas',
      path: '/user/notificacoes',
      badge: naoLidas,
    },
    { icon: PersonRoundedIcon, label: 'Perfil', path: userProfilePath(userId) },
  ];

  return (
    <Box
      component="nav"
      aria-label="Navegação principal"
      data-tour="user-nav"
      sx={{
        display: { xs: 'block', md: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: MOBILE.bottomNavZIndex,
        bgcolor: 'rgba(28,27,25,0.98)',
        borderTop: '1px solid rgba(242,238,231,0.12)',
        pb: MOBILE.safeAreaBottom,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        {itens.map(({ icon: Icon, label, path, badge = 0, tambemAtivo = [] }) => {
          const ativo =
            location.pathname === path ||
            tambemAtivo.some((prefixo) => location.pathname.startsWith(prefixo));

          return (
            <Box
              key={path}
              component="button"
              type="button"
              onClick={() => navigate(path)}
              aria-current={ativo ? 'page' : undefined}
              aria-label={badge > 0 ? `${label} (${badge} não lidas)` : label}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 56,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.3,
                px: 0.5,
                py: 0.6,
                border: 0,
                bgcolor: 'transparent',
                color: ativo ? COLORS.orange : 'rgba(242,238,231,0.75)',
                cursor: 'pointer',
                fontSize: '0.6rem',
                fontWeight: 800,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                transition: 'color 0.18s ease',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                '&:active': { transform: 'scale(0.96)' },
              }}
            >
              <Badge badgeContent={badge} color="error" max={99} overlap="circular">
                <Icon sx={{ fontSize: 22 }} />
              </Badge>
              {label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

UserBottomNav.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default UserBottomNav;
